import type { BasketOrderItem } from "../../hooks";
import {
  CaseSizeRuleCreateRequestType,
  SupplierGetResponseType,
  PriceTierDiscountType,
} from "@sellar-io/api-v2/types";
import {
  calculateItemTotal,
  filterDiscountApplicableItems,
  formatCurrency,
  formatDiscountAmount,
  getDiscountDescription,
  getItemName,
  groupItemsByCaseSizeRules,
} from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import { useBasketContext } from "../../hooks";
import { Icon } from "@sellar-io/components";

const MinimumOrderQuantityDiscountSummary = ({
  applicableOrderItemCount,
  discount,
  isDiscountActive,
  currency,
}: {
  applicableOrderItemCount: number;
  discount: {
    quantity: number;
    itemType: PriceTierDiscountType["itemType"];
    amount: number;
    type: PriceTierDiscountType["type"];
  };
  isDiscountActive: boolean;
  currency?: string;
}) => {
  const discountMinimumOrderQuantity = discount.quantity;

  const hasDiscountBeenSatisfied =
    applicableOrderItemCount >= discountMinimumOrderQuantity;

  const multipleItemTypeName = getItemName({
    itemType: discount.itemType,
    quantity: 2,
    extended: true,
  });

  // discount has been satisfied

  if (hasDiscountBeenSatisfied) {
    const singleItemTypeName = getItemName({
      itemType: discount.itemType,
      quantity: 1,
      extended: true,
    });

    const formattedDiscountAmount =
      discount.type === "percentage"
        ? `${discount.amount}%`
        : formatCurrency(discount.amount, currency);

    return (
      <div
        className={twMerge(
          "grid grid-cols-12 items-center",
          isDiscountActive ? "text-purple-700" : "text-purple-200 line-through",
        )}
      >
        <Icon
          icon={"CHECKBOX_MARKED_CIRCLE"}
          className={"col-span-1"}
          size={0.8}
        />
        <strong className={"col-span-11"}>
          {formattedDiscountAmount} off{" "}
          {discount.type === "perpetual-flat"
            ? `per ${singleItemTypeName}`
            : `${multipleItemTypeName === "items" ? "order items" : multipleItemTypeName}`}{" "}
          applied!
        </strong>
      </div>
    );
  }

  // some discount progress

  if (applicableOrderItemCount > 0) {
    const formattedDiscountAmount = formatDiscountAmount(discount, currency);

    const orderItemCountLeftToSatisfyDiscount =
      discountMinimumOrderQuantity - applicableOrderItemCount; // TODO: round down as applicableOrderItemCount could be decimal

    const dynamicItemTypeName = getItemName({
      itemType: discount.itemType,
      quantity: orderItemCountLeftToSatisfyDiscount,
      extended: true,
    });

    return (
      <div className={"grid grid-cols-12 items-center text-purple-700"}>
        <Icon
          icon={"CHECKBOX_BLANK_CIRCLE_OUTLINE"}
          className={"col-span-1"}
          size={0.8}
        />
        <div className={"col-span-11"}>
          {" "}
          Add <strong>{orderItemCountLeftToSatisfyDiscount} more</strong>{" "}
          {dynamicItemTypeName}, get {formattedDiscountAmount} off{" "}
          {discount.type === "perpetual-flat" ? " per item" : ""}
        </div>
      </div>
    );
  }

  // no discount progress

  return (
    <div>
      {getDiscountDescription({
        discount: { ...discount, minimumBasketValue: 0 },
        currency,
      })}
    </div>
  );
};

const MinimumBasketValueDiscountSummary = ({
  applicableOrderItemAmountSpent,
  discount,
  isDiscountActive,
  currency,
}: {
  applicableOrderItemAmountSpent: number;
  discount: {
    minimumBasketValue: number;
    itemType: PriceTierDiscountType["itemType"];
    amount: number;
    type: PriceTierDiscountType["type"];
  };
  isDiscountActive: boolean;
  currency?: string;
}) => {
  const discountMinimumBasketValue = discount.minimumBasketValue;

  const multipleItemTypeName = getItemName({
    itemType: discount.itemType,
    quantity: 2,
    extended: true,
  });

  // discount has been satisfied

  if (applicableOrderItemAmountSpent >= discountMinimumBasketValue) {
    const singleItemTypeName = getItemName({
      itemType: discount.itemType,
      quantity: 1,
      extended: true,
    });

    const formattedDiscountAmount =
      discount.type === "percentage"
        ? `${discount.amount}%`
        : formatCurrency(discount.amount, currency);

    return (
      <div
        className={twMerge(
          "grid grid-cols-12 items-center",
          isDiscountActive ? "text-purple-700" : "text-purple-200 line-through",
        )}
      >
        <Icon
          icon={"CHECKBOX_MARKED_CIRCLE"}
          className={"col-span-1"}
          size={0.8}
        />
        <strong className={"col-span-11"}>
          {formattedDiscountAmount} off{" "}
          {discount.type === "perpetual-flat"
            ? `per ${singleItemTypeName}`
            : `${multipleItemTypeName === "items" ? "order items" : multipleItemTypeName}`}{" "}
          applied!
        </strong>
      </div>
    );
  }

  // some discount progress

  if (applicableOrderItemAmountSpent > 0) {
    const formattedDiscountAmount = formatDiscountAmount(discount, currency);

    const formattedAmountLeftToSpendToSatisfyDiscount = formatCurrency(
      discountMinimumBasketValue - applicableOrderItemAmountSpent,
      currency,
    );

    return (
      <div className={"grid grid-cols-12 items-center text-purple-700"}>
        <Icon
          icon={"CHECKBOX_BLANK_CIRCLE_OUTLINE"}
          className={"col-span-1"}
          size={0.8}
        />
        <div className={"col-span-11"}>
          Spend{" "}
          <strong>{formattedAmountLeftToSpendToSatisfyDiscount} more</strong> on{" "}
          {multipleItemTypeName === "items"
            ? "any items"
            : multipleItemTypeName}
          , get {formattedDiscountAmount} off{" "}
          {discount.type === "perpetual-flat" ? " per item" : ""}
        </div>
      </div>
    );
  }

  // no discount progress

  return (
    <div>
      {getDiscountDescription({
        discount: { ...discount, quantity: 0 },
        currency,
      })}
    </div>
  );
};

export function OrderDiscountSummaryItem({
  discount,
  currency,
  caseSizeRuleList,
  orderItems,
  activeDiscountIds,
}: {
  discount: SupplierGetResponseType["CustomerZones"][number]["PriceTier"]["PriceTierDiscounts"][number];
  currency?: string;
  caseSizeRuleList: CaseSizeRuleCreateRequestType[];
  orderItems?: BasketOrderItem[];
  activeDiscountIds?: number[];
}) {
  const [{ order }] = useBasketContext();

  const discountOrderItems =
    (orderItems ?? order?.OrderItems)?.map((item) => {
      return {
        ...item,
        quantity: item.quantity,
        discountable: item.discountable,
        containerType: item.Product.containerType ?? null,
        price: item.price,
        volume: item.Product.volume ?? null,
        unit: item.Product.unit ?? null,
        packQuantity: item.Product.packQuantity ?? null,
      };
    }) ?? [];

  const applicableOrderItems = filterDiscountApplicableItems({
    orderItems: discountOrderItems,
    discountItemType: discount.itemType,
  });

  // check if volume discount is in list of active order volume discounts

  const isDiscountActive = activeDiscountIds
    ? activeDiscountIds.includes(discount.id)
    : !!order?.OrderDiscounts?.find(
        (orderDiscount) => orderDiscount.PriceTierDiscountId === discount.id,
      );

  // 'minimum order quantity' discount

  if (discount.quantity > 0) {
    const discountableOrderItemsGroupedByCaseSizeRules =
      groupItemsByCaseSizeRules({
        caseSizeRuleList: caseSizeRuleList,
        orderItemList: applicableOrderItems,
      }).map((item) => {
        // round down non integer item quantities down otherwise partials can be added to make 1
        return { ...item, quantity: Math.floor(item.quantity) };
      });

    const applicableOrderItemCount =
      discountableOrderItemsGroupedByCaseSizeRules.reduce(
        (total, item) => item.quantity + total,
        0,
      );

    return (
      <MinimumOrderQuantityDiscountSummary
        applicableOrderItemCount={applicableOrderItemCount}
        discount={discount}
        isDiscountActive={isDiscountActive}
        currency={currency}
      />
    );
  }

  // 'minimum basket value' discount

  const applicableOrderItemAmountSpent =
    calculateItemTotal(applicableOrderItems);

  return (
    <MinimumBasketValueDiscountSummary
      applicableOrderItemAmountSpent={applicableOrderItemAmountSpent}
      discount={discount}
      isDiscountActive={isDiscountActive}
      currency={currency}
    />
  );
}
